import React from "react";

import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import { getModuleThemeColors } from "@brexhq/next/src/components/pageBuilder/ModuleLayout";
import { Link } from "@brexhq/next/src/components/shared/Link/Link";
import { useFilteredArticles } from "@brexhq/next/src/hooks/useFilteredArticles/useFilteredArticles";
import { ArticleData } from "@brexhq/next/src/utils/repeatableContent/repeatableContentUtils";
import {
  BodyM,
  BodyS,
  SubtitleS,
} from "@brexhq/shared/components/Typography/Components";
import { ModuleThemeType } from "@brexhq/shared/types/ModuleTheme";
import type {
  ArticleListDisplayOptions,
  ArticleLinkListData,
} from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowItems/ArticleLinkList";
import { buildUrlFromSlug } from "@brexhq/shared/utils/buildUrlFromSlug";

import { DividerLine } from "../DividerLine/DividerLine";

export const getButtonText = (article: ArticleData) => {
  if (article.page._type === "pressRelease") {
    return "Read Now";
  }
  if (article.page.content.articleGroup?.name === "Webinars") {
    return "Watch Now";
  }
  return "Read Now";
};

const SingleArticle: React.FC<{
  article: ArticleData;
  theme: ModuleThemeType;
  displayOptions: ArticleListDisplayOptions;
}> = ({ article, theme, displayOptions }) => {
  const url = buildUrlFromSlug({
    pageType: article.page._type,
    slug: article.slug.current,
    parent:
      article.page._type === "contentHubArticle"
        ? article.page.content.articleGroup?.parent
        : undefined,
  });

  const { borders, textPrimary } = getModuleThemeColors(theme).standard;

  return (
    <Container direction={"column"} gap="xsml" margin={[0, 0, 0, 0]}>
      {displayOptions.showArticleGroup && (
        <>
          {article.page._type === "contentHubArticle" ? (
            article.page.content.articleGroup && (
              <BodyS color={textPrimary}>
                {article.page.content.articleGroup.name}
              </BodyS>
            )
          ) : (
            <BodyS color={textPrimary}>Press Release</BodyS>
          )}
        </>
      )}
      <SubtitleS color={textPrimary}>{article.page.meta.title}</SubtitleS>
      {displayOptions.showExcerpt && (
        <BodyM color={textPrimary}>{article.page.content.excerpt}</BodyM>
      )}
      <Link
        modifier={"arrow"}
        to={url}
        label={getButtonText(article)}
        component="ArticleLinkList"
        theme={theme}
      />

      <DividerLine style="line break" color={borders} marginTop="none" />
    </Container>
  );
};

export const ArticleLinkList: React.FC<{
  data: ArticleLinkListData;
  theme: ModuleThemeType;
}> = ({ data, theme }) => {
  const filteredArticles = useFilteredArticles(data._key);
  const borderColor = getModuleThemeColors(theme).standard.borders;
  return (
    <Container direction={"column"} gap="xsml" margin={[0, 0, "sml", 0]}>
      <DividerLine marginTop="sml" style="line break" color={borderColor} />

      {filteredArticles
        .slice(0, data.displayOptions.maxArticles)
        .map((article) => (
          <SingleArticle
            key={article.page.id}
            article={article}
            theme={theme}
            displayOptions={data.displayOptions}
          />
        ))}
    </Container>
  );
};
